import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import { Pill, TrendingUp } from 'lucide-react';
import { usePharmacies } from '../context/PharmacyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function Login({ onLogin }) {
  const [activeTab, setActiveTab] = useState('login'); // 'login' ou 'register'
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [registerData, setRegisterData] = useState({
    pharmacy_name: '',
    address: '',
    phone: '',
    email: '',
    owner_username: '',
    owner_password: '',
    owner_full_name: '',
    owner_email: '',
    owner_phone: ''
  });
  const [loading, setLoading] = useState(false);
  const { updateUserPharmacies } = usePharmacies();

  // Clear stale session data when login page is shown
  useEffect(() => {
    sessionStorage.removeItem('session_token');
    sessionStorage.removeItem('pharmacy_user');
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      const userData = response.data;
      
      // Store session token IMMEDIATELY so subsequent API calls work
      if (userData.session_token) {
        sessionStorage.setItem('session_token', userData.session_token);
      }
      
      // Vérifier que l'utilisateur a des pharmacies actives (sauf super admin)
      if (userData.role !== 'super_admin' && (!userData.pharmacies || userData.pharmacies.length === 0)) {
        toast.error('Aucune pharmacie associée à ce compte');
        setLoading(false);
        return;
      }
      
      // Mettre à jour le context
      if (userData.role === 'super_admin') {
        try {
          const allPharmaciesRes = await axios.get(`${API}/pharmacies`, {
            headers: { 'X-User-Id': userData.id }
          });
          
          const allPharmaciesFormatted = allPharmaciesRes.data
            .filter(p => p.status === 'active')
            .map(p => ({
              pharmacy_id: p.id,
              pharmacy_name: p.name,
              role: 'super_admin'
            }));
          
          updateUserPharmacies(allPharmaciesFormatted);
        } catch (error) {
          console.error('Error loading pharmacies for super admin:', error);
        }
      } else {
        updateUserPharmacies(userData.pharmacies);
      }
      
      onLogin(userData);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur de connexion';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register-pharmacy`, registerData);
      toast.success(response.data.message || 'Demande envoyée avec succès !', {
        description: 'Votre pharmacie sera activée après validation par l\'administrateur.',
        duration: 6000
      });
      setActiveTab('login');
      // Reset form
      setRegisterData({
        pharmacy_name: '',
        address: '',
        phone: '',
        email: '',
        owner_username: '',
        owner_password: '',
        owner_full_name: '',
        owner_email: '',
        owner_phone: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-500 via-blue-600 to-indigo-700 flex items-center justify-center p-4">
      <div className="w-full max-w-md fade-in">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
              <Pill className="w-8 h-8 text-cyan-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">MIZAN</h1>
          <p className="text-cyan-100 text-lg">Gestion Comptable Professionnelle</p>
        </div>

        <Card className="shadow-2xl">
          <CardHeader>
            <div className="flex space-x-1 border-b">
              <button
                onClick={() => setActiveTab('login')}
                className={`flex-1 pb-3 text-sm font-medium transition-colors ${
                  activeTab === 'login'
                    ? 'text-cyan-600 border-b-2 border-cyan-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Se connecter
              </button>
              <button
                onClick={() => setActiveTab('register')}
                className={`flex-1 pb-3 text-sm font-medium transition-colors ${
                  activeTab === 'register'
                    ? 'text-cyan-600 border-b-2 border-cyan-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                S'inscrire
              </button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {activeTab === 'login' ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-username">Nom d'utilisateur *</Label>
                  <Input
                    id="login-username"
                    type="text"
                    value={loginData.username}
                    onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">Mot de passe *</Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                  disabled={loading}
                >
                  {loading ? 'Connexion...' : 'Se connecter'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleRegister} className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-gray-700">Informations Pharmacie</h3>
                  <div>
                    <Label htmlFor="pharmacy_name">Nom de la pharmacie *</Label>
                    <Input
                      id="pharmacy_name"
                      value={registerData.pharmacy_name}
                      onChange={(e) => setRegisterData({ ...registerData, pharmacy_name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address">Adresse</Label>
                    <Input
                      id="address"
                      value={registerData.address}
                      onChange={(e) => setRegisterData({ ...registerData, address: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="phone">Téléphone</Label>
                      <Input
                        id="phone"
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-3 pt-3 border-t">
                  <h3 className="font-semibold text-sm text-gray-700">Votre compte</h3>
                  <div>
                    <Label htmlFor="owner_full_name">Nom complet *</Label>
                    <Input
                      id="owner_full_name"
                      value={registerData.owner_full_name}
                      onChange={(e) => setRegisterData({ ...registerData, owner_full_name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner_username">Nom d'utilisateur *</Label>
                    <Input
                      id="owner_username"
                      value={registerData.owner_username}
                      onChange={(e) => setRegisterData({ ...registerData, owner_username: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner_password">Mot de passe *</Label>
                    <Input
                      id="owner_password"
                      type="password"
                      value={registerData.owner_password}
                      onChange={(e) => setRegisterData({ ...registerData, owner_password: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                  disabled={loading}
                >
                  {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  Votre pharmacie sera activée après validation par l'administrateur
                </p>
              </form>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <div className="flex items-center justify-center space-x-6 text-white/80 text-sm">
            <div className="flex items-center">
              <TrendingUp className="w-4 h-4 mr-2" />
              <span>Suivi des revenus</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
